import React from 'react';
import { intlShape } from '../../../util/reactIntl';
import { generateCitySlug, generateStoreSlug } from '../../../util/seo';
import { array, bool, object, string } from 'prop-types';
import css from '../ListingPage.module.css';
import { H4 } from '../../../components';
import { propTypes } from '../../../util/types';
import ListCard from '../../SearchPage/AlgoliaSearchPage/MainSearchContent/Card/ListCard';

const MAX_LISTINGS = 4;

const AuthorListings = ({
  intl,
  authorListings,
  authorListingsInProgress,
  authorListingsError,
  author,
  isVerified,
  rating,
  currentUserCurrency,
  exchangeRates,
  history,
  routes,
  queryID,
  onActivateListing = () => { },
  currentUserCountryCode,
  locale,
  hasLocationAvailable,
  categoryLabel,
  storeName,
}) => {
  const hasListings = Array.isArray(authorListings) && authorListings.length > 0;
  const hasAuthor = author && author.id;

  if (!hasListings || !hasAuthor) return null;

  const profile = author.attributes?.profile || {};
  const { displayName, publicData } = profile;
  const city = publicData?.city;
  const citySlug = generateCitySlug(publicData?.storeAddress?.city || city);

  // Individual owner vs. store account - same branching pattern as AboutOwner.js.
  const isStoreAccount = !!storeName;
  const authorListingsTitle = isStoreAccount
    ? intl.formatMessage({ id: 'AuthorListings.titleStore' }, { storeName })
    : intl.formatMessage({ id: 'AuthorListings.titleOwner' }, { displayName });
  const category =
    categoryLabel || intl.formatMessage({ id: 'AuthorListings.genericCategory' });
  const authorListingsSubtitle = intl.formatMessage(
    { id: 'AuthorListings.subtitle' },
    { category }
  );

  const visibleListings = authorListings.slice(0, MAX_LISTINGS);

  return (
    <section className={css.similarItemSection} aria-labelledby="author-listings-heading">
      <H4 id="author-listings-heading" className={css.itemTitle}>
        {authorListingsTitle}
      </H4>
      <p className={css.itemSubTitle}>{authorListingsSubtitle}</p>

      <div className={css.authorListingsGrid}>
        {visibleListings.map(l => (
          <ListCard
            key={l.id.uuid}
            rental={{
              objectID: l.id.uuid,
              title: l.attributes.title,
              price: l.attributes.price,
              user: {
                displayName: l.author.attributes.profile.displayName,
                firstName: l.author.attributes.profile.firstName,
                lastName: l.author.attributes.profile.lastName,
                publicData: l.author.attributes.profile.publicData,
              },
              publicData: l.attributes.publicData,
              distance: l.attributes.distance,
              _geoloc: l.attributes.geolocation,
            }}
            currentUserCurrency={currentUserCurrency}
            exchangeRates={exchangeRates}
            history={history}
            routes={routes}
            queryID={queryID}
            additional={l.additional}
            onActivateListing={onActivateListing}
            currentUserCountryCode={currentUserCountryCode}
            locale={locale}
            hasLocationAvailable={hasLocationAvailable}
            isCritical={false}
            // "carousel" layout mode fills 100% of its container (correct for a CSS
            // grid cell here) - "grid" mode hard-codes 50% width for the flex-wrap
            // 2-per-row search results grid, which would halve these cards' width.
            layout="carousel"
          />
        ))}
      </div>
    </section>
  );
};

AuthorListings.propTypes = {
  intl: intlShape.isRequired,
  authorListings: array.isRequired,
  authorListingsInProgress: bool,
  authorListingsError: propTypes.error,
  author: propTypes.user.isRequired,
  isVerified: bool,
  rating: propTypes.rating,
  currentUserCurrency: propTypes.currency,
  exchangeRates: propTypes.exchangeRates,
  history: object.isRequired,
  routes: object.isRequired,
  queryID: propTypes.uuid,
  onActivateListing: propTypes.func,
  currentUserCountryCode: propTypes.string,
  locale: propTypes.string,
  hasLocationAvailable: bool,
  categoryLabel: string,
  storeName: string,
};

export default AuthorListings;
